<template>
  <div class="page-container">
    <PageHeader title="用户管理">
      <template #actions>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openAddDialog">新增用户</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="fetchUserList">刷新</el-button>
      </template>
    </PageHeader>

    <TablePanel>
      <el-table v-loading="loading" :data="userList" stripe>
        <el-table-column prop="username" label="登录名" min-width="140" />
        <el-table-column prop="realName" label="姓名" min-width="120">
          <template slot-scope="{ row }">{{ row.realName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="role" label="角色" min-width="100">
          <template slot-scope="{ row }">
            <el-tag v-if="row.role" size="small" :type="row.role === 'admin' ? 'danger' : 'info'">{{ row.role }}</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="{ row }">
            <!-- 后端限制不能停用自己：自己的行置灰 -->
            <el-switch
              :value="row.status === 1"
              :disabled="row.id === currentUserId"
              active-color="#10b981"
              @change="val => handleStatusChange(row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column label="最近登录" min-width="170">
          <template slot-scope="{ row }">{{ formatDateTime(row.lastLoginTime) }}</template>
        </el-table-column>
        <el-table-column label="创建时间" min-width="170">
          <template slot-scope="{ row }">{{ formatDateTime(row.createTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="120" align="center" class-name="aj-table-cell">
          <template slot-scope="{ row }">
            <!-- 操作列统一走 business-actions 的操作链接体系 -->
            <el-link
              v-if="row.id !== currentUserId"
              class="aj-table-operation-link"
              :class="row.status === 1 ? 'aj-table-operation-link--warning' : 'aj-table-operation-link--success'"
              :underline="false"
              @click="handleStatusChange(row, row.status !== 1)"
            >{{ row.status === 1 ? '停用' : '启用' }}</el-link>
            <span v-else class="qw-tag qw-tag--info">当前账号</span>
          </template>
        </el-table-column>
      </el-table>
    </TablePanel>

    <!-- 新增用户弹窗 -->
    <el-dialog title="新增用户" :visible.sync="addDialogVisible" width="440px" :close-on-click-modal="false">
      <el-form ref="addForm" :model="addForm" :rules="addRules" label-width="80px" size="small">
        <el-form-item label="登录名" prop="username">
          <el-input v-model.trim="addForm.username" placeholder="登录账号（唯一）" />
        </el-form-item>
        <el-form-item label="姓名" prop="realName">
          <el-input v-model.trim="addForm.realName" placeholder="真实姓名（可选）" />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input v-model.trim="addForm.password" type="password" show-password placeholder="至少 6 位" />
        </el-form-item>
      </el-form>
      <template slot="footer">
        <el-button size="small" @click="addDialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" :loading="submitting" @click="submitAdd">确 定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import TablePanel from '@/components/TablePanel.vue'
import { getUserList, addUser, updateUserStatus } from '@/api/userList'
import { formatDateTime } from '@/utils/format'

// 用户管理：列表 / 新增 / 启用停用（对应后端 /qw-admin/user/* 接口）
export default {
    name: 'UserList',
    components: { PageHeader, TablePanel },
    data () {
        return {
            loading: false,
            submitting: false,
            userList: [],
            addDialogVisible: false,
            addForm: { username: '', realName: '', password: '' },
            addRules: {
                username: [{ required: true, message: '请输入登录名', trigger: 'blur' }],
                password: [
                    { required: true, message: '请输入密码', trigger: 'blur' },
                    { min: 6, message: '密码至少 6 位', trigger: 'blur' }
                ]
            }
        }
    },
    computed: {
        // 当前登录用户 id：用于禁用「停用自己」
        currentUserId () {
            const user = this.$store.state.user.userInfo || {}
            return user.id
        }
    },
    created () {
        this.fetchUserList()
    },
    methods: {
        formatDateTime,
        async fetchUserList () {
            this.loading = true
            try {
                const res = await getUserList()
                this.userList = res.data || []
            } catch (error) {
                // 错误提示已由 request 拦截器统一弹出
            } finally {
                this.loading = false
            }
        },
        openAddDialog () {
            this.addForm = { username: '', realName: '', password: '' }
            this.addDialogVisible = true
            this.$nextTick(() => {
                this.$refs.addForm && this.$refs.addForm.clearValidate()
            })
        },
        submitAdd () {
            this.$refs.addForm.validate(async valid => {
                if (!valid) return
                this.submitting = true
                try {
                    await addUser(this.addForm)
                    this.$message.success('用户创建成功')
                    this.addDialogVisible = false
                    this.fetchUserList()
                } catch (error) {
                    // 错误提示已由 request 拦截器统一弹出
                } finally {
                    this.submitting = false
                }
            })
        },
        // 启用/停用切换：status 1 启用 0 停用
        handleStatusChange (row, enabled) {
            const status = enabled ? 1 : 0
            const action = enabled ? '启用' : '停用'
            this.$confirm(`确定${action}用户「${row.realName || row.username}」吗？`, '提示', { type: 'warning' })
                .then(async () => {
                    await updateUserStatus(row.id, status)
                    this.$message.success(`已${action}`)
                    this.fetchUserList()
                })
                .catch(() => {})
        }
    }
}
</script>
